import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { GroupChannel as GroupChannelType } from "@sendbird/chat/lib/__definition";
import "@sendbird/uikit-react/dist/index.css";
import { Spin, Typography } from '@arco-design/web-react';

import { endpoints } from "../../api/endpoints";
import { ConversationsList } from "./components/ConversationsList/ConversationsList";
import { Conversation } from "./components/Conversation/Conversation";
import { routes } from "../../routes/routes";
import { RootState } from "../../store/store";
import { setUser } from "../../store/userSlice";
import { useBreakpoint } from "../../utils/hooks/useBreakpoint";
import { persistLastChannelUrl, useAutoSelectChannel } from "./utils/useAutoSelectChannel";
import { trackEvent } from "../../analytics/track";
import { useMessageEditTextCenter } from "./utils/useMessageEditTextCenter";
import "./Messages.css";

/**
 * Sendbird UI hooks must not run until Layout has mounted <SendbirdProvider>
 * (after /config + logged-in user). Keep those hooks in this child only.
 */
const MessagesWithSendbird = ({
  selectedChannel,
  onSelectChannel,
  onCloseConversation,
  onChannelUpdated,
  isMobile,
}: {
  selectedChannel: GroupChannelType | null;
  onSelectChannel: (channel: GroupChannelType | null) => void;
  onCloseConversation: () => void;
  onChannelUpdated: (channel: GroupChannelType) => void;
  isMobile: boolean;
}) => {
  useAutoSelectChannel(selectedChannel, onSelectChannel, !isMobile);
  useMessageEditTextCenter();

  return (
    <div className="messages-page">
      {isMobile ? (
        <div className="messages-page__mobile-panel">
          {selectedChannel ? (
            <Conversation
              activeConversation={selectedChannel}
              onCloseConversation={onCloseConversation}
              onChannelUpdated={onChannelUpdated}
            />
          ) : (
            <ConversationsList
              onConversationSelected={onSelectChannel}
              selectedConversation={selectedChannel}
            />
          )}
        </div>
      ) : (
        <div className="messages-page__desktop">
          <ConversationsList
            onConversationSelected={onSelectChannel}
            selectedConversation={selectedChannel}
          />
          <div className="messages-page__conversation-panel">
            <Conversation
              activeConversation={selectedChannel}
              onCloseConversation={onCloseConversation}
              onChannelUpdated={onChannelUpdated}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const Messages = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  const sendbirdAppId = useSelector((state: RootState) => state.config.sendbirdAppId);
  const configLoaded = useSelector((state: RootState) => state.config.configLoaded);
  const [selectedChannel, setSelectedChannel] = useState<GroupChannelType | null>(null);
  const [authReady, setAuthReady] = useState(!!user);
  const { isMobile } = useBreakpoint('mobile');

  const handleSelectChannel = useCallback((channel: GroupChannelType | null) => {
    setSelectedChannel(channel);
    if (channel?.url) {
      persistLastChannelUrl(channel.url);
      trackEvent("message_open");
    }
  }, []);

  useEffect(() => {
    if (user) {
      setAuthReady(true);
      return;
    }

    let isMounted = true;
    axios
      .get(endpoints.whoami)
      .then((response) => {
        if (isMounted) {
          dispatch(setUser({ ...response.data }));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) {
          setAuthReady(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch, user]);

  const handleCloseConversation = () => {
    setSelectedChannel(null);
  };

  const handleChannelUpdated = (channel: GroupChannelType) => {
    setSelectedChannel(channel);
  };

  if (!authReady || !configLoaded) {
    return <Spin style={{ margin: '120px auto' }} size={32} />;
  }

  if (!user) {
    return <Navigate to={routes.LOGIN} replace />;
  }

  if (!sendbirdAppId) {
    return (
      <div style={{ display: 'gap', padding: '48px 12px', wordBreak: "break-word" }}>
        <Typography.Title heading={5} style={{ wordBreak: "break-word" }}>
          {t('messages.no_messages_error')}
        </Typography.Title>
        <Typography.Paragraph style={{ wordBreak: "break-word" }}>
          {t('messages.refresh_page_text')}
        </Typography.Paragraph>
        <Typography.Paragraph style={{ wordBreak: "break-word" }}>
          {t('messages.contact_admin_text')}
        </Typography.Paragraph>
        <Typography.Title heading={6} style={{ wordBreak: "break-word" }}>
          {t('messages.appreciationText')}
        </Typography.Title>
      </div>
    );
  }

  return (
    <MessagesWithSendbird
      selectedChannel={selectedChannel}
      onSelectChannel={handleSelectChannel}
      onCloseConversation={handleCloseConversation}
      onChannelUpdated={handleChannelUpdated}
      isMobile={isMobile}
    />
  );
};
